import { Injectable } from '@angular/core';
import { TextColorType } from '../edit-form/crud-view-text-color-type.enum';
import { TextStyleType } from '../edit-form/crud-view-text-style-type.enum';
import { FieldFormattingColorUtil } from './field-formatting-color.util';
import { IFormattableFieldItem, IFormattableConfig } from './field-formatting-item.interface';

/**
 * Generic service for handling field formatting operations
 * Works with any data structure that implements IFormattableFieldItem
 * Can be used in any component that needs field formatting functionality
 */
@Injectable({
  providedIn: 'root'
})
export class FieldFormattingService {
  /**
   * Get text color value (delegates to utility)
   */
  getTextColorValue(colorType: TextColorType | string | undefined): string {
    return FieldFormattingColorUtil.getTextColorValue(colorType);
  }

  /**
   * Get text color indicator (delegates to utility)
   */
  getTextColorIndicator(colorType: TextColorType | string | undefined): string {
    return FieldFormattingColorUtil.getTextColorIndicator(colorType);
  }

  /**
   * Get field text color from config
   * Generic method that works with any IFormattableConfig
   */
  getFieldTextColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string
  ): TextColorType | string | undefined {
    return config?.items?.find((item: T) => item.fieldName === fieldName)?.textColor;
  }

  /**
   * Get field background color from config (for field labels)
   */
  getFieldBackgroundColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string
  ): TextColorType | string | undefined {
    const item = config?.items?.find((item: T) => item.fieldName === fieldName);
    return item?.cssStyle?.['label-background-color'];
  }

  /**
   * Get field value background color from config (for field values)
   */
  getFieldValueBackgroundColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string
  ): TextColorType | string | undefined {
    const item = config?.items?.find((item: T) => item.fieldName === fieldName);
    return item?.cssStyle?.['background-color'] || item?.cssStyle?.['backgroundColor'];
  }

  /**
   * Get field value text color from config
   */
  getFieldValueTextColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string
  ): TextColorType | string | undefined {
    return config?.items?.find((item: T) => item.fieldName === fieldName)?.valueTextColor;
  }

  /**
   * Get field text style from config
   */
  getFieldTextStyle<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string
  ): TextStyleType | undefined {
    return config?.items?.find((item: T) => item.fieldName === fieldName)?.textStyle;
  }

  /**
   * Get field value text style from config
   */
  getFieldValueTextStyle<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string
  ): TextStyleType | undefined {
    return config?.items?.find((item: T) => item.fieldName === fieldName)?.valueTextStyle;
  }

  /**
   * Update field text color in config
   * Generic method that works with any IFormattableConfig
   */
  updateFieldTextColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string,
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items?.length || !fieldName) {
      return config;
    }

    const items = config.items.map((item: T) => {
      if (item.fieldName === fieldName) {
        const updatedItem = { ...item };
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedItem.textColor = color as any;
        } else {
          delete updatedItem.textColor;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Update field background color in config (for field labels)
   */
  updateFieldBackgroundColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string,
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items?.length || !fieldName) {
      return config;
    }

    const items = config.items.map((item: T) => {
      if (item.fieldName === fieldName) {
        const updatedItem = { ...item };
        if (!updatedItem.cssStyle) {
          updatedItem.cssStyle = {};
        }
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          const colorValue = typeof color === 'string' && color.startsWith('#')
            ? color
            : this.getTextColorValue(color);
          if (colorValue) {
            updatedItem.cssStyle['label-background-color'] = colorValue;
          }
        } else {
          if (updatedItem.cssStyle['label-background-color']) {
            delete updatedItem.cssStyle['label-background-color'];
          }
          // Remove cssStyle if empty
          if (Object.keys(updatedItem.cssStyle).length === 0) {
            delete updatedItem.cssStyle;
          }
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Update field value background color in config (for field values)
   */
  updateFieldValueBackgroundColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string,
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items?.length || !fieldName) {
      return config;
    }

    const items = config.items.map((item: T) => {
      if (item.fieldName === fieldName) {
        const updatedItem = { ...item };
        if (!updatedItem.cssStyle) {
          updatedItem.cssStyle = {};
        }
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          const colorValue = typeof color === 'string' && color.startsWith('#')
            ? color
            : this.getTextColorValue(color);
          if (colorValue) {
            updatedItem.cssStyle['background-color'] = colorValue;
          }
        } else {
          if (updatedItem.cssStyle['background-color']) {
            delete updatedItem.cssStyle['background-color'];
          }
          if (updatedItem.cssStyle['backgroundColor']) {
            delete updatedItem.cssStyle['backgroundColor'];
          }
          // Remove cssStyle if empty
          if (Object.keys(updatedItem.cssStyle).length === 0) {
            delete updatedItem.cssStyle;
          }
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Update field value text color in config
   */
  updateFieldValueTextColor<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string,
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items?.length || !fieldName) {
      return config;
    }

    const items = config.items.map((item: T) => {
      if (item.fieldName === fieldName) {
        const updatedItem = { ...item };
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedItem.valueTextColor = color as any;
        } else {
          delete updatedItem.valueTextColor;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Update field text style in config
   */
  updateFieldTextStyle<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string,
    style: TextStyleType | undefined
  ): any {
    if (!config?.items?.length || !fieldName) {
      return config;
    }

    const items = config.items.map((item: T) => {
      if (item.fieldName === fieldName) {
        const updatedItem = { ...item };
        if (style) {
          updatedItem.textStyle = style;
        } else {
          delete updatedItem.textStyle;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Update field value text style in config
   */
  updateFieldValueTextStyle<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fieldName: string,
    style: TextStyleType | undefined
  ): any {
    if (!config?.items?.length || !fieldName) {
      return config;
    }

    const items = config.items.map((item: T) => {
      if (item.fieldName === fieldName) {
        const updatedItem = { ...item };
        if (style) {
          updatedItem.valueTextStyle = style;
        } else {
          delete updatedItem.valueTextStyle;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Check if all fields have the same property value
   * Generic method that works with any field type
   */
  hasAllFieldsSameProperty<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    getProperty: (config: any, fieldName: string) => TextColorType | string | TextStyleType | undefined
  ): boolean {
    if (!fields?.length || !fields[0]?.fieldName) {
      return false;
    }
    const firstValue = getProperty(config, fields[0].fieldName);
    return fields.every(field => field.fieldName && getProperty(config, field.fieldName) === firstValue);
  }

  /**
   * Get common property value that all fields share (if they all have the same)
   */
  getAllFieldsProperty<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    getProperty: (config: any, fieldName: string) => TextColorType | string | TextStyleType | undefined,
    hasAllSame: boolean
  ): TextColorType | string | TextStyleType | undefined {
    if (!hasAllSame || !fields?.length || !fields[0]?.fieldName) {
      return undefined;
    }
    return getProperty(config, fields[0].fieldName);
  }

  /**
   * Apply color to all fields
   */
  applyColorToAllFields<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items || !fields.length) {
      return config;
    }

    const fieldNames = new Set(fields.map(f => f.fieldName).filter(Boolean));
    const items = config.items.map((item: T) => {
      if (item.fieldName && fieldNames.has(item.fieldName)) {
        const updatedItem = { ...item };
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedItem.textColor = color as any;
        } else {
          delete updatedItem.textColor;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Apply background color to all fields
   */
  applyBackgroundColorToAllFields<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items || !fields.length) {
      return config;
    }

    const fieldNames = new Set(fields.map(f => f.fieldName).filter(Boolean));
    const items = config.items.map((item: T) => {
      if (item.fieldName && fieldNames.has(item.fieldName)) {
        const updatedItem = { ...item };
        if (!updatedItem.cssStyle) {
          updatedItem.cssStyle = {};
        }
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          const colorValue = typeof color === 'string' && color.startsWith('#')
            ? color
            : this.getTextColorValue(color);
          if (colorValue) {
            updatedItem.cssStyle['label-background-color'] = colorValue;
          }
        } else {
          if (updatedItem.cssStyle['label-background-color']) {
            delete updatedItem.cssStyle['label-background-color'];
          }
          // Remove cssStyle if empty
          if (Object.keys(updatedItem.cssStyle).length === 0) {
            delete updatedItem.cssStyle;
          }
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Apply background color to all field values
   */
  applyBackgroundColorToAllFieldValues<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items || !fields.length) {
      return config;
    }

    const fieldNames = new Set(fields.map(f => f.fieldName).filter(Boolean));
    const items = config.items.map((item: T) => {
      if (item.fieldName && fieldNames.has(item.fieldName)) {
        const updatedItem = { ...item };
        if (!updatedItem.cssStyle) {
          updatedItem.cssStyle = {};
        }
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          const colorValue = typeof color === 'string' && color.startsWith('#')
            ? color
            : this.getTextColorValue(color);
          if (colorValue) {
            updatedItem.cssStyle['background-color'] = colorValue;
          }
        } else {
          if (updatedItem.cssStyle['background-color']) {
            delete updatedItem.cssStyle['background-color'];
          }
          if (updatedItem.cssStyle['backgroundColor']) {
            delete updatedItem.cssStyle['backgroundColor'];
          }
          // Remove cssStyle if empty
          if (Object.keys(updatedItem.cssStyle).length === 0) {
            delete updatedItem.cssStyle;
          }
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Apply color to all field values
   */
  applyColorToAllFieldValues<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    color: TextColorType | string | undefined
  ): any {
    if (!config?.items || !fields.length) {
      return config;
    }

    const fieldNames = new Set(fields.map(f => f.fieldName).filter(Boolean));
    const items = config.items.map((item: T) => {
      if (item.fieldName && fieldNames.has(item.fieldName)) {
        const updatedItem = { ...item };
        if (color && (typeof color === 'string' && color.startsWith('#')) || (color !== TextColorType.Default)) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          updatedItem.valueTextColor = color as any;
        } else {
          delete updatedItem.valueTextColor;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Calculate new style based on current style and toggle type
   */
  calculateNewStyle(currentStyle: TextStyleType | undefined, styleType: TextStyleType): TextStyleType | undefined {
    if (styleType === TextStyleType.Bold) {
      if (currentStyle === TextStyleType.Bold) {
        return undefined;
      } else if (currentStyle === TextStyleType.BoldItalic) {
        return TextStyleType.Italic;
      } else if (currentStyle === TextStyleType.Italic) {
        return TextStyleType.BoldItalic;
      } else {
        return TextStyleType.Bold;
      }
    } else if (styleType === TextStyleType.Italic) {
      if (currentStyle === TextStyleType.Italic) {
        return undefined;
      } else if (currentStyle === TextStyleType.BoldItalic) {
        return TextStyleType.Bold;
      } else if (currentStyle === TextStyleType.Bold) {
        return TextStyleType.BoldItalic;
      } else {
        return TextStyleType.Italic;
      }
    } else if (styleType === TextStyleType.Underline) {
      return currentStyle === TextStyleType.Underline ? undefined : TextStyleType.Underline;
    }
    return undefined;
  }

  /**
   * Toggle style for all fields
   */
  toggleAllFieldsStyle<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    currentStyle: TextStyleType | undefined,
    styleType: TextStyleType
  ): any {
    if (!config?.items || !fields.length) {
      return config;
    }

    const newStyle = this.calculateNewStyle(currentStyle, styleType);
    const fieldNames = new Set(fields.map(f => f.fieldName));

    const items = config.items.map(item => {
      if (fieldNames.has(item.fieldName)) {
        const updatedItem = { ...item };
        if (newStyle) {
          updatedItem.textStyle = newStyle;
        } else {
          delete updatedItem.textStyle;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }

  /**
   * Toggle style for all field values
   */
  toggleAllFieldValuesStyle<T extends IFormattableFieldItem>(
    config: IFormattableConfig<T> | any,
    fields: T[],
    currentStyle: TextStyleType | undefined,
    styleType: TextStyleType
  ): any {
    if (!config?.items || !fields.length) {
      return config;
    }

    const newStyle = this.calculateNewStyle(currentStyle, styleType);
    const fieldNames = new Set(fields.map(f => f.fieldName));

    const items = config.items.map(item => {
      if (fieldNames.has(item.fieldName)) {
        const updatedItem = { ...item };
        if (newStyle) {
          updatedItem.valueTextStyle = newStyle;
        } else {
          delete updatedItem.valueTextStyle;
        }
        return updatedItem;
      }
      return item;
    });

    return { ...config, items };
  }
}

